import './chat.css';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError, errorText } from '../../api';
import { ChatComposer, isChatFile } from '../../client/chat/common/Composer';
import { loadChatFile, useOpenFile } from '../../client/chat/common/files';
import { ChatMessages } from '../../client/chat/common/Messages';
import { threadId } from '../../client/chat/common/store';
import type { ChatMessage, KanzleiThread, SendResult } from '../../client/chat/common/types';
import { useMarkRead } from '../../client/chat/common/useMarkRead';
import { initials } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { useKanzlei } from '../context';
import { kanzleiChat, kanzleiOpenThread } from './data';
import { FileDialog } from './FileDialog';

// Tab „Chat“ in der Mandantenansicht. Mitarbeiter sehen nur ihren eigenen Chat.
// Der Admin wählt oben zwischen „Mein Chat“ und den Chats der anderen (nur lesen). Gewählter Chat steht in ?mit=.
export function ChatTab({ clientId }: { clientId: string }) {
  const { me } = useKanzlei();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const mit = params.get('mit') ?? undefined;
  const view = useApi<KanzleiThread>(`/api/kanzlei/chat/${clientId}${mit ? `?mit=${encodeURIComponent(mit)}` : ''}`, {
    live: ['chat'],
    clientId,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const focusMine = useRef(false);
  const [file, setFile] = useOpenFile();
  const [busy, setBusy] = useState(false);
  const d = view.data;
  const selected = d?.selected ?? null;

  kanzleiOpenThread.useRegister(selected ? threadId(clientId, selected) : null);
  useMarkRead(threadId(clientId, selected ?? ''), d?.messages, (until) =>
    api.post(`/api/kanzlei/chat/${clientId}/read`, { staffId: selected, until }),
  );

  // Fremden Chat gibt es nicht (mehr) oder darf man nicht sehen: zurück zum eigenen.
  useEffect(() => {
    if (view.error && mit) setParams({}, { replace: true });
  }, [view.error, mit, setParams]);

  // „In „Mein Chat“ antworten“: nach dem Wechsel ins Eingabefeld
  useEffect(() => {
    if (focusMine.current && selected === me.user.id) {
      focusMine.current = false;
      inputRef.current?.focus();
    }
  }, [selected, me.user.id]);

  if (!d) return view.error && !mit ? <p className="note">{view.error}</p> : <div className="spinner" aria-label="Lädt" />;

  const thread = d.threads.find((t) => t.staffId === d.selected) ?? d.threads[0]!;
  const mine = d.selected === me.user.id;
  const contact = d.client.contactName;

  const choose = (staffId: string) => setParams(staffId === me.user.id ? {} : { mit: staffId }, { replace: true });

  const sent = (r: SendResult) => {
    view.setData((v) => (v && !v.messages.some((m) => m.id === r.message.id) ? { ...v, messages: [...v.messages, r.message] } : v));
    kanzleiChat.reload();
  };

  const sendText = async (text: string) => sent(await api.post<SendResult>(`/api/kanzlei/chat/${clientId}/messages`, { text }));

  const sendFile = async (f: File) => {
    if (!isChatFile(f)) {
      toast(`${f.name}: nur PDF oder Foto`);
      return;
    }
    toast('Datei wird gesendet …');
    const form = new FormData();
    form.append('file', f, f.name);
    try {
      sent(await api.post<SendResult>(`/api/kanzlei/chat/${clientId}/files`, form));
      toast(`Datei gesendet: ${f.name}`);
    } catch (err) {
      // 413 auch dann, wenn schon der Proxy vor der App die Datei abgelehnt hat (ohne Text der App)
      toast(`${f.name}: ${err instanceof ApiError && err.status === 413 ? 'Die Datei ist zu groß für den Chat.' : errorText(err)}`);
    }
  };

  const openFile = async (m: ChatMessage) => {
    if (!m.file || busy) return;
    setBusy(true);
    try {
      const loaded = await loadChatFile(`/api/kanzlei/chat/${clientId}/files/${m.file.id}`, m.file);
      setFile({ ...loaded, meta: `Datei im Chat · ${m.sender === 'client' ? contact : thread.name}` });
    } catch {
      toast('Datei kann nicht geöffnet werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="chat-tab">
      {me.user.isAdmin && (
        <div className="chpills" role="group" aria-label="Chats">
          {d.threads.map((t) => {
            const on = t.staffId === d.selected;
            return (
              <button
                key={t.staffId}
                type="button"
                className={`btn sm${on ? ' dark' : ''}`}
                aria-pressed={on}
                onClick={() => choose(t.staffId)}
              >
                {t.mine ? 'Mein Chat' : t.name} <span className="note">{t.role}</span>
                {t.unread > 0 && !on && (
                  <span className="pill p-warn">
                    {t.unread}
                    <span className="sr-only"> ungelesen</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <div className="chat">
        <div className="chat-head">
          <span className="avc" aria-hidden="true">
            {initials(contact)}
          </span>
          <div>
            <b>{contact}</b>
            <small>
              {d.client.name} · Chat mit {mine ? 'Ihnen' : thread.name}
            </small>
          </div>
        </div>
        <ChatMessages
          messages={d.messages}
          isMine={(m) => m.sender !== 'client'}
          onOpenFile={(m) => void openFile(m)}
          empty={
            mine
              ? `Noch keine Nachrichten mit ${contact}. Schreiben Sie hier, der Mandant sieht es in der App unter „Nachrichten“.`
              : `Noch keine Nachrichten mit ${contact}.`
          }
        />
        {mine && (
          <ChatComposer
            key={threadId(clientId, d.selected)}
            clipTitle="Datei anhängen (PDF oder Foto)"
            inputRef={inputRef}
            onSendText={sendText}
            onSendFile={(f) => void sendFile(f)}
          />
        )}
      </div>
      {mine ? (
        <p className="note chat-note">
          Das ist Ihr eigener Chat mit {contact}. Der Mandant sieht ihn in der App unter „Nachrichten“ als „{me.user.name}“.
        </p>
      ) : (
        <p className="note chat-note">
          Chat von {thread.name} mit {contact}. Als Admin lesen Sie mit, schreiben können Sie in „Mein Chat“.{' '}
          <button
            type="button"
            className="btn sm dark"
            onClick={() => {
              focusMine.current = true;
              choose(me.user.id);
            }}
          >
            In „Mein Chat“ antworten
          </button>
        </p>
      )}
      {file && <FileDialog file={file} clientName={d.client.name} onClose={() => setFile(null)} />}
    </div>
  );
}
